import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { toast } from "react-toastify";

type Length = "short" | "medium" | "long";

interface ContentTypeConfig {
  rules: string;
  defaultTone: string;
  defaultLength: Length;
  defaultLocale: string;
}

const EMPTY: ContentTypeConfig = {
  rules: "",
  defaultTone: "",
  defaultLength: "medium",
  defaultLocale: "en",
};

// Content types that support AI generation. Extend here when more are added.
const GENERATABLE_TYPES: Array<{ id: string; label: string }> = [
  { id: "insights", label: "Insights" },
];

const LENGTH_OPTIONS: Array<{ id: Length; label: string; hint: string }> = [
  { id: "short", label: "Short", hint: "~300–500 words" },
  { id: "medium", label: "Medium", hint: "~600–900 words" },
  { id: "long", label: "Long", hint: "~1200–1800 words" },
];

const AiGenerationSettings: React.FC = () => {
  const { project } = useParams<{ project: string }>();
  const { user, firebaseUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState<Record<string, ContentTypeConfig>>({});

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    getDoc(doc(db, "content", project, "_config", "ai_generation"))
      .then((snap) => {
        const data = snap.exists() ? snap.data() : {};
        const next: Record<string, ContentTypeConfig> = {};
        for (const t of GENERATABLE_TYPES) {
          const raw = data[t.id] || {};
          next[t.id] = {
            rules: raw.rules || "",
            defaultTone: raw.defaultTone || "",
            defaultLength: (raw.defaultLength as Length) || "medium",
            defaultLocale: raw.defaultLocale || "en",
          };
        }
        setCfg(next);
      })
      .catch((err) => console.error("[AiGenerationSettings] Load error:", err))
      .finally(() => setLoading(false));
  }, [project]);

  const update = (type: string, patch: Partial<ContentTypeConfig>) => {
    setCfg((prev) => ({ ...prev, [type]: { ...(prev[type] || EMPTY), ...patch } }));
  };

  const save = async () => {
    if (!project) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "content", project, "_config", "ai_generation"),
        {
          ...cfg,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        },
        { merge: true }
      );
      toast.success("Saved");
      logActivity(project, {
        action: "edit",
        contentType: "ai_generation",
        docTitle: "AI Generation",
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[AiGenerationSettings] Save error:", err);
      toast.error("Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="general">
      <div className="general__header">
        <h2 className="general__title">AI Generation</h2>
        <div className="general__actions">
          <button className="editor__save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {GENERATABLE_TYPES.map((t) => {
        const c = cfg[t.id] || EMPTY;
        return (
          <div key={t.id} className="general__section">
            <h3 className="general__section-title">{t.label} Rules</h3>
            <p className="general__section-hint">
              Authoritative contract injected as a system prompt for every generated {t.label.toLowerCase()} article —
              frontmatter schema, allowed values, structure, voice, forbidden phrasing.
              Per-article fields in the Generate modal override these defaults.
            </p>

            <label className="cms-modal__field">
              <span>Rules (system prompt)</span>
              <textarea
                value={c.rules}
                onChange={(e) => update(t.id, { rules: e.target.value })}
                placeholder={`Frontmatter:\n---\ntitle: "…" (≤70 chars, sentence case)\ndescription: "…" (≤160 chars, one-sentence hook)\nbadge: "Research" | "Guide" | "Myth" | "Science"\n---\n\nVoice: calm, evidence-aware, non-promotional. Short paragraphs.\nAudience: curious non-expert adult.\nForbidden: exclamation marks, emojis, "we" / "our", medical-advice phrasing.\nStructure: single H1 (= title), then ## sections, ### sub-sections.\nCitations: "research suggests…" — never invent numbers.`}
                rows={14}
              />
            </label>

            <div className="cms-modal__row">
              <label className="cms-modal__field">
                <span>Default tone</span>
                <input
                  type="text"
                  value={c.defaultTone}
                  onChange={(e) => update(t.id, { defaultTone: e.target.value })}
                  placeholder="friendly, conversational, evidence-based"
                />
              </label>
              <label className="cms-modal__field">
                <span>Default locale</span>
                <input
                  type="text"
                  value={c.defaultLocale}
                  onChange={(e) => update(t.id, { defaultLocale: e.target.value })}
                  placeholder="en"
                />
              </label>
            </div>

            <div className="cms-modal__field">
              <span>Default length</span>
              <div className="cms-modal__radio-group">
                {LENGTH_OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className={`cms-modal__radio ${c.defaultLength === opt.id ? "cms-modal__radio--active" : ""}`}
                  >
                    <input
                      type="radio"
                      name={`length-${t.id}`}
                      checked={c.defaultLength === opt.id}
                      onChange={() => update(t.id, { defaultLength: opt.id })}
                    />
                    <span className="cms-modal__radio-label">{opt.label}</span>
                    <span className="cms-modal__radio-hint">{opt.hint}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AiGenerationSettings;
